import React, { useMemo, useEffect, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { grey as muiGrey } from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

/* ── Extend MUI palette types to include numbered shade tokens ────────── */
declare module '@mui/material/styles' {
  interface PaletteColor { 50: string; }
  interface SimplePaletteColorOptions { 50?: string; }
}

const breakpoints = {
  values: { xs: 0, sm: 600, md: 900, lg: 1024, xl: 1536 },
};

function getDesignTokens(mode) {
  const isDark = mode === 'dark';
  
  const tokens = {
    light: {
      bg: '#F8FAFC',
      paper: '#FFFFFF',
      text: '#0F172A',
      textMuted: '#334155',
      divider: '#E5E7EB',
    },
    dark: {
      bg: '#0B1020',        // matches --kind-bg-dark
      paper: '#1A2332',      // matches dark --tile-bg
      text: '#E6EAF2',       // matches --kind-fg-dark
      textMuted: '#A3A3A3',  // grey[700] for readable secondary text
      divider: '#1F2937',    // matches --kind-border-dark
    },
  };
  
  const colors = isDark ? tokens.dark : tokens.light;
  
  return {
    palette: {
      mode,
      primary:   { main: '#57BDE4', 50: isDark ? '#0E1D28' : '#E8F6FC' },
      secondary: { main: '#4DB9AE', dark: '#429E95', 50: isDark ? '#12302D' : '#EAF8F6' },
      info:      { main: '#0288d1', 50: isDark ? '#071929' : '#E1F5FE' },
      success:   { main: '#2e7d32', 50: isDark ? '#0A1F0C' : '#E8F5E9' },
      warning:   { main: '#ed6c02', 50: isDark ? '#1F1400' : '#FFF3E0' },
      error:     { main: '#d32f2f', 50: isDark ? '#1F0A0A' : '#FFEBEE' },
      background: {
        default: colors.bg,
        paper: colors.paper,
      },
      text: {
        primary: colors.text,
        secondary: colors.textMuted,
      },
      divider: colors.divider,
      grey: isDark ? {
        50: '#14161A',
        100: '#1A1D23',
        200: '#20232A',
        300: '#262626',
        400: '#3A3A3A',
        500: '#525252',
        600: '#737373',
        700: '#A3A3A3',
        800: '#C9CED6',
        900: '#F5F5F5',
      } : muiGrey,
    },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      // Mobile (≤767px, matches useIsMobile): shrink MUI's desktop-scaled
      // heading defaults — h4 alone defaults to 2.125rem.
      h1: { fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial', fontWeight: 600, '@media (max-width:767px)': { fontSize: '2rem' } },
      h2: { fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial', fontWeight: 600, '@media (max-width:767px)': { fontSize: '1.75rem' } },
      h3: { fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial', fontWeight: 600, '@media (max-width:767px)': { fontSize: '1.5rem' } },
      h4: { fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial', fontWeight: 500, '@media (max-width:767px)': { fontSize: '1.25rem' } },
      h5: { '@media (max-width:767px)': { fontSize: '1.125rem' } },
      h6: { '@media (max-width:767px)': { fontSize: '1rem' } },
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightSemiBold: 600,
    },
    breakpoints,
    shape: { borderRadius: 14 },
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
      popover: 1600, // Popovers, Menus, Selects, Autocomplete dropdowns - above modals
    },
    components: {
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          variant: 'outlined',
          size: 'medium' as const,
        },
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              minHeight: '48px', // Touch-friendly on mobile
              fontSize: '16px', // Prevents iOS zoom on focus
              '@media (min-width: 900px)': {
                minHeight: '44px', // More compact on desktop
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '16px', // Prevents iOS zoom on focus
              padding: '10px 14px', // Tighter padding
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '@media (max-width:767px)': {
              padding: '12px', // tighter cards on mobile
              '&:last-child': { paddingBottom: '12px' },
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            '@media (max-width:767px)': {
              padding: '12px 16px', // tighter dialogs on mobile
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            minHeight: '48px', // Touch-friendly on mobile
            fontSize: '16px', // Prevents iOS zoom
            '@media (min-width: 900px)': {
              minHeight: '44px', // More compact on desktop
            },
          },
          input: {
            fontSize: '16px', // Prevents iOS zoom on all inputs
            '&::placeholder': {
              fontSize: '16px',
              opacity: 0.6,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          fullWidth: true,
          displayEmpty: true, // Show placeholder when empty
        },
        styleOverrides: {
          select: {
            minHeight: '48px', // Touch-friendly on mobile
            fontSize: '16px', // Prevents iOS zoom on focus
            display: 'flex',
            alignItems: 'center',
            paddingTop: '10px',
            paddingBottom: '10px',
            '@media (min-width: 900px)': {
              minHeight: '44px', // More compact on desktop
            },
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          fullWidth: true,
        },
        styleOverrides: {
          inputRoot: {
            minHeight: '48px', // Touch-friendly on mobile
            fontSize: '16px', // Prevents iOS zoom on focus
            '& .MuiInputBase-input': {
              fontSize: '16px',
              padding: '10px 14px',
            },
            '@media (min-width: 900px)': {
              minHeight: '44px', // More compact on desktop
            },
          },
          input: {
            fontSize: '16px', // Prevents iOS zoom
          },
          option: {
            minHeight: '48px', // Touch-friendly on mobile
            padding: '10px 16px',
            fontSize: '16px',
            '@media (min-width: 900px)': {
              minHeight: '44px', // More compact on desktop
            },
          },
          listbox: {
            maxHeight: '60vh', // Better mobile scrolling
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: '48px', // Touch-friendly on mobile
            fontSize: '16px',
            textTransform: 'none', // Better readability
            '@media (min-width: 900px)': {
              minHeight: '44px', // More compact on desktop
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          fullWidth: true,
          size: 'medium' as const,
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            minHeight: '16px', // Reduced from 20px
            marginTop: '2px', // Reduced from 4px
            fontSize: '14px',
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            fontSize: '16px',
            fontWeight: 500,
            '@media (min-width: 900px)': {
              fontSize: '14px',
            },
          },
          shrink: {
            fontSize: '16px',
            '@media (min-width: 900px)': {
              fontSize: '14px',
            },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          notched: true,
        },
        styleOverrides: {
          notchedOutline: {
            '& > legend': {
              maxWidth: '100%',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--font-heading': '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
            '--font-brand': 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

            '--kind-primary': '#57BDE4',
            '--kind-secondary': '#66B32B',
            '--kind-tertiary': '#4DB9AE',
            '--kind-gradient': 'linear-gradient(90deg, #57BDE4, #66B32B)',

            '--kind-bg-light': '#FFFFFF',
            '--kind-bg-dark': '#0B1020',
            '--kind-fg-light': '#0F172A',
            '--kind-fg-dark': '#E6EAF2',
            '--kind-border-light': '#E5E7EB',
            '--kind-border-dark': '#1F2937',

            '--tile-gap': '12px',
            '--tile-radius': '14px',
            '--icon-size': '24px',
            '--icon-offset': '8px',

            '--c1': '#57BDE4',
            '--c1-light': '#CFEEFD',
            '--c1-dark': '#2c8db7',

            '--c2': '#66B32B',
            '--c2-light': '#E2F4D2',
            '--c2-dark': '#3f7e19',

            '--c3': '#4DB9AE',
            '--c3-light': '#D3F0ED',
            '--c3-dark': '#2c8a81',

            '--kind-gradient-light': 'linear-gradient(90deg, #57BDE4, #66B32B)',
            '--kind-gradient-dark': 'linear-gradient(90deg, #2A6B80, #3F6E1A)',

            '--tile-bg': '#FFFFFF',
            '--tile-fg': '#0F172A',
            '--tile-border': '#E5E7EB',
          },
          'html.dark, html[data-theme="dark"]': {
            '--tile-bg': '#1A2332',
            '--tile-fg': '#E6EAF2',
            '--tile-border': '#1F2937',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            // Keep accordion headers visible below the sticky header
            // when scrollIntoView is triggered by the global accordion
            // scroll handler in Layout.tsx.
            scrollMarginTop: 'calc(var(--header-height-full, 56px) + 8px)',
          },
        },
      },
    },
  };
}

function detectInitialMode() {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    if (document.documentElement.classList.contains('dark')) return 'dark';
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

export function AppThemeProvider({ children, mode = undefined }: { children: React.ReactNode; mode?: 'light' | 'dark' }) {
  const [internalMode, setInternalMode] = useState(mode || detectInitialMode());

  useEffect(() => {
    if (mode) { setInternalMode(mode); return; }
    const obs = new MutationObserver(() => {
      const attr = document.documentElement.getAttribute('data-theme');
      if (attr === 'dark' || attr === 'light') {
        setInternalMode(attr);
      } else {
        const hasDarkClass = document.documentElement.classList.contains('dark');
        setInternalMode(hasDarkClass ? 'dark' : 'light');
      }
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => obs.disconnect();
  }, [mode]);

  const theme = useMemo(() => createTheme(getDesignTokens(internalMode) as Parameters<typeof createTheme>[0]), [internalMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{
        ':root': {
          '--tile-min-row': '120px',
          '--tile-bg': theme.palette.background.paper,
          '--tile-fg': theme.palette.text.primary,
          '--tile-border': theme.palette.divider,
        },
        'body': {
          backgroundColor: theme.palette.background.default,
        },
        '#root': {
          backgroundColor: theme.palette.background.default,
        },
        '@media (min-width: 600px)': {
          ':root': {
            '--tile-min-row': '140px',
          },
        },
        '@media (min-width: 1024px)': {
          ':root': {
            '--tile-min-row': '110px',
            '--tile-gap': '8px',
          },
        },
      }} />
      {children}
    </ThemeProvider>
  );
}
